import { offerBillingLabel } from '../../lib/offerBilling';
import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useScope } from '@workspace/scope-context';
import { AlertTriangle, Pencil, Trash2 } from 'lucide-react';
import {
  CommerceCatalogWorkspace,
  PricebookDesignerWorkspace,
  toast,
  type CommerceHelpCalloutViewModel,
  type CommerceSelectOptionViewModel,
  type PricebookChargeType,
  type PricebookControlledFocusTarget,
  type PricebookDirectoryQueryViewModel,
  type PricebookEditorDestination,
  type PricebookEditorFormValue,
  type PricebookEditorViewModel,
  type PricebookQuoteEstimatorOptionsViewModel,
  type PricebookQuoteReadinessLinkViewModel,
  type PricebookPriceScope,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  Checkbox,
  DescriptionText,
  HorizontalStack,
  RegistryStatus,
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { useApiGatewayAccess } from '@/lib/useApiGatewayAccess';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const PRICEBOOK_SORT_FIELDS = ['catalog_id', 'coverage', 'created_at', 'currency', 'name', 'pricing_strategy', 'selling_tenant_id', 'status', 'updated_at'] as const;
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import {
  CommerceBreadcrumbHeader,
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  equipmentLeaseRecurringOfferType,
  formatAllowedTermMonthsText,
  metadataRecordToEntries,
  parseAllowedTermMonthsText,
} from '@/views/commerce/catalogHierarchyShared';
import {
  buildPricebookCreateRoute,
  buildPricebookEditorRoute,
  buildPricebooksListRoute,
  parsePricebookRoute,
} from '@/lib/commerce-routing';
import {
  createPricebook,
  deletePricebook,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  getPricebook,
  listAccessPointLayers,
  listCatalogs,
  listPricebooks,
  listServiceabilityZones,
  updatePricebook,
  type Catalog,
  type Pricebook,
} from '@/lib/commerce';
import { buildTenantSelectorOptions } from '@/views/commerce/selectorOptions';
import {
  formatMinorCurrencyInput,
  parseCurrencyInputToMinor,
} from '@/features/commerce-admin/currency';
import {
  buildPricebookDirectory,
  buildDraftOfferPrice,
  buildPricebookMutation,
  buildPricebookReviewChangeGroups,
  buildPricebookValidationIssues,
  normalizePricebookDirectoryQuery,
  remapOfferPriceValidationAfterDiscard,
  toggleExpandedProductId,
} from '@/features/commerce-admin/pricebookEditorController';
import {
  buildQuotePolicyReviewChangeGroup,
  buildQuotePolicyViewModel,
  createDefaultQuotePolicyFormValue,
  hydrateQuotePolicy,
  loadQuotePolicyEstimatorOptions,
  validateQuotePolicy,
} from '@/features/commerce-admin/quotePolicyController';

type ValidationErrors = Record<string, string>;

type PriceFilter = 'priced' | 'unpriced' | 'attention';

const initialDirectoryQuery: PricebookDirectoryQueryViewModel = {
  search: '',
  view: 'all_current',
  productIds: [],
  offerTypes: [],
  sort: 'product_asc',
  activeFilterCount: 0,
};

const initialEstimatorOptions: PricebookQuoteEstimatorOptionsViewModel = {
  status: 'unavailable',
  options: [],
  selectedServiceAccountIds: [],
  unresolvedSelectedServiceAccountIds: [],
  isReadOnly: true,
  message: 'Estimator credentials load when the pricebook editor opens.',
};

function emptyPricebookForm(): PricebookEditorFormValue {
  return {
    name: '',
    status: 'draft',
    currency: 'USD',
    catalogId: '',
    sellingTenantId: '',
    pricingStrategy: 'standard',
    leaseTerms: {
      allowedTermMonthsText: '',
    },
    offerPrices: [],
    feePrices: [],
    quotePolicy: createDefaultQuotePolicyFormValue(),
    metadata: [],
  };
}

function toDatetimeLocal(value?: string | null): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function deriveOfferChargeType(offerType?: string | null): PricebookChargeType {
  switch (offerType?.trim()) {
    case 'subscription':
    case 'rent':
    case 'installment':
    case 'trial':
    case equipmentLeaseRecurringOfferType:
      return 'recurring';
    case 'purchase':
    case 'customer_owned':
    default:
      return 'one_time';
  }
}

function mapPricebookToForm(pricebook: Pricebook): PricebookEditorFormValue {
  const offerPrices = pricebook.offer_prices?.length
    ? pricebook.offer_prices.map(row => ({
        rowId: row.row_id || row.offer_id,
        offerId: row.offer_id,
        productId: pricebook.offer_targets?.find(target => target.offer_id === row.offer_id)?.product_id || null,
        variantId: row.variant_id,
        productLabel: row.product_label,
        variantLabel: row.variant_label,
        offerLabel: row.offer_label,
        offerType: row.offer_type || 'offer',
        termMonths: row.term_months ?? null,
        chargeType: (row.charge_type as PricebookChargeType) || deriveOfferChargeType(row.offer_type),
        amount: row.amount || '',
        percentage: '',
        currency: row.currency || pricebook.currency || 'USD',
        billingPeriod: offerBillingLabel(row.billing_period_months),
        leasePricing: row.lease_pricing
          ? {
              purchaseCostAmount: formatMinorCurrencyInput(
                row.lease_pricing.purchase_cost_amount_minor,
                row.lease_pricing.purchase_cost_currency || pricebook.currency || 'USD',
              ),
              purchaseCostCurrency: row.lease_pricing.purchase_cost_currency || pricebook.currency || 'USD',
              intendedSalePriceAmount: formatMinorCurrencyInput(
                row.lease_pricing.intended_sale_price_amount_minor,
                row.lease_pricing.intended_sale_price_currency || pricebook.currency || 'USD',
              ),
              intendedSalePriceCurrency: row.lease_pricing.intended_sale_price_currency || pricebook.currency || 'USD',
              leaseRateBps: row.lease_pricing.lease_rate_bps ?? null,
              minimumUpfrontPaymentAmount: formatMinorCurrencyInput(
                row.lease_pricing.minimum_upfront_payment_amount_minor,
                row.lease_pricing.intended_sale_price_currency || pricebook.currency || 'USD',
              ),
              maximumUpfrontPaymentAmount: formatMinorCurrencyInput(
                row.lease_pricing.maximum_upfront_payment_amount_minor,
                row.lease_pricing.intended_sale_price_currency || pricebook.currency || 'USD',
              ),
            }
          : null,
        fees: (row.fees || []).map(fee => ({
          rowId: fee.row_id || `${row.offer_id}-fee-${fee.fee_code || fee.fee_label}`,
          feeLabel: fee.fee_label || '',
          feeCode: fee.fee_code || '',
          chargeType: (fee.charge_type as PricebookChargeType) || 'one_time',
          amount: fee.amount || '',
          percentage: fee.percentage || '',
          currency: fee.currency || pricebook.currency || 'USD',
          billingPeriod: fee.billing_period || '',
          startsAt: toDatetimeLocal(fee.starts_at),
          endsAt: toDatetimeLocal(fee.ends_at),
          summary: fee.summary || '',
        })),
        startsAt: toDatetimeLocal(row.starts_at),
        endsAt: toDatetimeLocal(row.ends_at),
        summary: row.summary || '',
      }))
    : (pricebook.variant_prices || []).map(row => ({
        rowId: row.row_id || row.variant_id,
        offerId: row.variant_id,
        productId: pricebook.variant_targets?.find(target => target.variant_id === row.variant_id)?.product_id || null,
        variantId: row.variant_id,
        productLabel: row.product_label,
        variantLabel: row.variant_label,
        offerLabel: row.variant_label,
        offerType: 'variant',
        chargeType: (row.charge_type as PricebookChargeType) || 'one_time',
        amount: row.amount || '',
        percentage: row.percentage || '',
        currency: row.currency || pricebook.currency || 'USD',
        billingPeriod: row.billing_period || '',
        fees: [],
        startsAt: toDatetimeLocal(row.starts_at),
        endsAt: toDatetimeLocal(row.ends_at),
        summary: row.summary || '',
      }));

  return {
    name: pricebook.name,
    status: pricebook.status,
    currency: pricebook.currency || 'USD',
    catalogId: pricebook.catalog_id || '',
    sellingTenantId: pricebook.selling_tenant_id || '',
    pricingStrategy: (pricebook.pricing_strategy as PricebookEditorFormValue['pricingStrategy']) || 'standard',
    leaseTerms: {
      allowedTermMonthsText: formatAllowedTermMonthsText(pricebook.lease_terms?.allowed_term_months),
    },
    offerPrices,
    feePrices: (pricebook.fee_prices || []).map(row => ({
      rowId: row.row_id || row.fee_id,
      feeId: row.fee_id,
      feeLabel: row.fee_label,
      appliesToLabel: row.applies_to_label,
      chargeType: (row.charge_type as PricebookChargeType) || 'one_time',
      amount: row.amount || '',
      percentage: row.percentage || '',
      currency: row.currency || pricebook.currency || 'USD',
      startsAt: toDatetimeLocal(row.starts_at),
      endsAt: toDatetimeLocal(row.ends_at),
      summary: row.summary || '',
    })),
    quotePolicy: hydrateQuotePolicy(pricebook.quote_calculation_policy),
    metadata: metadataRecordToEntries(pricebook.metadata),
  };
}

function mapOfferTargetOptions(pricebook: Pricebook): CommerceSelectOptionViewModel[] {
  if (pricebook.offer_targets?.length) {
    return pricebook.offer_targets.map(target => ({
      value: target.offer_id,
      label: target.offer_label,
      description: `${target.product_label} • ${target.variant_label}`,
      helperText: [offerBillingLabel(target.billing_period_months), target.term_months ? `${target.term_months} mo` : null]
        .filter(Boolean)
        .join(' • ') || null,
      badge: target.offer_type || 'Offer',
      metadata: {
        productId: target.product_id,
        variantId: target.variant_id,
        termMonths: target.term_months ?? null,
        billingPeriod: offerBillingLabel(target.billing_period_months),
        offerType: target.offer_type || '',
        productLabel: target.product_label,
        variantLabel: target.variant_label,
      },
      keywords: [
        target.offer_key,
        target.offer_label,
        target.offer_type,
        target.term_months ? `${target.term_months}` : null,
        target.term_months ? `${target.term_months}mo` : null,
        target.variant_label,
        target.product_label,
        target.status,
        target.is_default ? 'default' : null,
      ].filter(Boolean) as string[],
    }));
  }
  return (pricebook.variant_targets || []).map(target => ({
    value: target.variant_id,
    label: target.variant_label,
    description: `${target.product_label} • ${target.variant_label}`,
    helperText: null,
    badge: 'Variant',
    metadata: {
      productLabel: target.product_label,
      variantLabel: target.variant_label,
    },
    keywords: [
      target.variant_key,
      target.variant_label,
      target.product_label,
      target.status,
    ].filter(Boolean) as string[],
  }));
}

function getOfferPriceCount(pricebook: Pricebook): number {
  if (pricebook.offer_prices?.length) {
    return pricebook.offer_prices.length;
  }
  return pricebook.variant_prices?.length || 0;
}

function mapFeeTargetOptions(pricebook: Pricebook): CommerceSelectOptionViewModel[] {
  return (pricebook.fee_targets || []).map(target => ({
    value: target.fee_id,
    label: target.fee_label,
    description: target.applies_to_label,
    helperText: target.charge_timing,
    badge: target.required ? 'Required' : 'Optional',
    keywords: [target.fee_type, target.variant_id, target.applies_to_label].filter(Boolean) as string[],
  }));
}

function validateForm(
  form: PricebookEditorFormValue,
  options: {
    validateOfferRows?: boolean;
    validateFeeRows?: boolean;
    shouldValidateOfferRow?: (row: PricebookEditorFormValue['offerPrices'][number], index: number) => boolean;
    shouldValidateFeeRow?: (row: PricebookEditorFormValue['feePrices'][number], index: number) => boolean;
  } = {},
): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!form.name.trim()) {
    errors.name = 'Pricebook name is required.';
  }

  if (options.validateOfferRows === false && options.validateFeeRows === false) {
    return errors;
  }

  const offerRowsToValidate = form.offerPrices
    .map((row, index) => ({ row, index }))
    .filter(({ row, index }) => options.validateOfferRows !== false && (options.shouldValidateOfferRow?.(row, index) ?? true));
  const hasLeaseRows = offerRowsToValidate.some(({ row }) => row.offerType === equipmentLeaseRecurringOfferType);
  if (hasLeaseRows && parseAllowedTermMonthsText(form.leaseTerms.allowedTermMonthsText).length === 0) {
    errors['leaseTerms.allowedTermMonthsText'] = 'Enter at least one allowed lease term.';
  }

  offerRowsToValidate.forEach(({ row, index }) => {
    if (!row.offerId.trim()) {
      errors[`offerPrices.${index}.offerId`] = 'Choose an offer target.';
    }
    if (row.offerType === equipmentLeaseRecurringOfferType) {
      const purchaseCurrency = row.leasePricing?.purchaseCostCurrency || form.currency || 'USD';
      const saleCurrency = row.leasePricing?.intendedSalePriceCurrency || form.currency || 'USD';
      if (!row.leasePricing?.purchaseCostAmount || Number(row.leasePricing.purchaseCostAmount) <= 0) {
        errors[`offerPrices.${index}.leasePricing.purchaseCostAmount`] = 'Enter a purchase cost.';
      } else {
        try {
          parseCurrencyInputToMinor(String(row.leasePricing.purchaseCostAmount), purchaseCurrency, 'Purchase cost');
        } catch (error) {
          errors[`offerPrices.${index}.leasePricing.purchaseCostAmount`] = error instanceof Error ? error.message : 'Enter a valid purchase cost.';
        }
      }
      if (!row.leasePricing?.purchaseCostCurrency?.trim()) {
        errors[`offerPrices.${index}.leasePricing.purchaseCostCurrency`] = 'Enter a purchase cost currency.';
      }
      if (!row.leasePricing?.intendedSalePriceAmount || Number(row.leasePricing.intendedSalePriceAmount) <= 0) {
        errors[`offerPrices.${index}.leasePricing.intendedSalePriceAmount`] = 'Enter an intended sale price.';
      } else {
        try {
          parseCurrencyInputToMinor(String(row.leasePricing.intendedSalePriceAmount), saleCurrency, 'Intended sale price');
        } catch (error) {
          errors[`offerPrices.${index}.leasePricing.intendedSalePriceAmount`] = error instanceof Error ? error.message : 'Enter a valid intended sale price.';
        }
      }
      if (!row.leasePricing?.intendedSalePriceCurrency?.trim()) {
        errors[`offerPrices.${index}.leasePricing.intendedSalePriceCurrency`] = 'Enter an intended sale price currency.';
      } else if (row.leasePricing.intendedSalePriceCurrency.trim().toUpperCase() !== form.currency.trim().toUpperCase()) {
        errors[`offerPrices.${index}.leasePricing.intendedSalePriceCurrency`] = 'Intended sale price currency must match the pricebook currency.';
      }
      if (row.leasePricing?.leaseRateBps === null || row.leasePricing?.leaseRateBps === undefined || row.leasePricing.leaseRateBps < 0) {
        errors[`offerPrices.${index}.leasePricing.leaseRateBps`] = 'Enter a lease rate.';
      }
      const intendedSalePrice = Number(row.leasePricing?.intendedSalePriceAmount);
      if (intendedSalePrice > 0 && row.leasePricing?.minimumUpfrontPaymentAmount != null && Number(row.leasePricing.minimumUpfrontPaymentAmount) > intendedSalePrice) {
        errors[`offerPrices.${index}.leasePricing.minimumUpfrontPaymentAmount`] = 'Minimum upfront payment cannot exceed the intended sale price.';
      }
      if (intendedSalePrice > 0 && row.leasePricing?.maximumUpfrontPaymentAmount != null && Number(row.leasePricing.maximumUpfrontPaymentAmount) > intendedSalePrice) {
        errors[`offerPrices.${index}.leasePricing.maximumUpfrontPaymentAmount`] = 'Maximum upfront payment cannot exceed the intended sale price.';
      }
      for (const [field, label] of [
        ['minimumUpfrontPaymentAmount', 'Minimum upfront payment'],
        ['maximumUpfrontPaymentAmount', 'Maximum upfront payment'],
      ] as const) {
        const value = row.leasePricing?.[field];
        if (value == null) continue;
        try {
          parseCurrencyInputToMinor(String(value), saleCurrency, label);
        } catch (error) {
          errors[`offerPrices.${index}.leasePricing.${field}`] = error instanceof Error ? error.message : `Enter a valid ${label.toLowerCase()}.`;
        }
      }
    } else if (!row.amount?.trim()) {
      errors[`offerPrices.${index}.amount`] = 'Enter an amount.';
    }
    row.fees?.forEach((fee, feeIndex) => {
      if (!fee.feeLabel.trim()) {
        errors[`offerPrices.${index}.fees.${feeIndex}.feeLabel`] = 'Enter a fee label.';
      }
      if (fee.chargeType === 'percentage') {
        if (!fee.percentage?.trim()) {
          errors[`offerPrices.${index}.fees.${feeIndex}.percentage`] = 'Enter a percentage.';
        }
      } else if (fee.chargeType !== 'included' && !fee.amount?.trim()) {
        errors[`offerPrices.${index}.fees.${feeIndex}.amount`] = 'Enter an amount.';
      }
      if (fee.chargeType === 'recurring' && !fee.billingPeriod?.trim()) {
        errors[`offerPrices.${index}.fees.${feeIndex}.billingPeriod`] = 'Choose a billing period.';
      }
    });
  });

  if (options.validateFeeRows !== false) form.feePrices.forEach((row, index) => {
    if (!(options.shouldValidateFeeRow?.(row, index) ?? true)) return;
    if (!row.feeId.trim()) {
      errors[`feePrices.${index}.feeId`] = 'Choose a fee target.';
    }
    if (row.chargeType === 'percentage') {
      if (!row.percentage?.trim()) {
        errors[`feePrices.${index}.percentage`] = 'Enter a percentage.';
      }
    } else if (row.chargeType !== 'included' && !row.amount?.trim()) {
      errors[`feePrices.${index}.amount`] = 'Enter an amount.';
    }
  });

  return errors;
}

function quotePolicyValidationErrors(
  form: PricebookEditorFormValue,
  estimatorOptions: PricebookQuoteEstimatorOptionsViewModel,
): ValidationErrors {
  return Object.fromEntries(validateQuotePolicy(form.quotePolicy, estimatorOptions).map(issue => [issue.field, issue.message]));
}

export default function PricebooksPage() {
  const { activeTenant, organizations } = useScope();
  const apiGatewayAccess = useApiGatewayAccess();
  const navigation = useNavigation();
  const pricebookRoute = useMemo(() => parsePricebookRoute(navigation.path), [navigation.path]);
  const [pricebooks, setPricebooks] = useState<Pricebook[]>([]);
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [catalogLoadError, setCatalogLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: ['active', 'archived', 'draft'] },
    sortFields: PRICEBOOK_SORT_FIELDS,
  });
  const searchQuery = query.search;
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const [totalPricebooks, setTotalPricebooks] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const requestSequence = useRef(0);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [targetHydrationNotice, setTargetHydrationNotice] = useState<string | null>(null);
  const [offerTargetOptions, setOfferTargetOptions] = useState<CommerceSelectOptionViewModel[]>([]);
  const [feeTargetOptions, setFeeTargetOptions] = useState<CommerceSelectOptionViewModel[]>([]);
  const [estimatorOptions, setEstimatorOptions] = useState<PricebookQuoteEstimatorOptionsViewModel>(initialEstimatorOptions);
  const [quoteReadinessLinks, setQuoteReadinessLinks] = useState<PricebookQuoteReadinessLinkViewModel[]>([]);
  const [form, setForm] = useState<PricebookEditorFormValue>(emptyPricebookForm());
  const [initialForm, setInitialForm] = useState<PricebookEditorFormValue | null>(null);
  const [editorPricebook, setEditorPricebook] = useState<Pricebook | null>(null);
  const [activeDestination, setActiveDestination] = useState<PricebookEditorDestination>('settings');
  const [activePriceScope, setActivePriceScope] = useState<PricebookPriceScope>('product_prices');
  const [directoryQuery, setDirectoryQuery] = useState<PricebookDirectoryQueryViewModel>(initialDirectoryQuery);
  const [expandedProductIds, setExpandedProductIds] = useState<string[]>([]);
  const [markedForRemovalRowIds, setMarkedForRemovalRowIds] = useState<string[]>([]);
  const [controlledFocusTarget, setControlledFocusTarget] = useState<PricebookControlledFocusTarget | null>(null);
  const [priceFilter, setPriceFilter] = useState<PriceFilter>('priced');
  const [selectedPriceRowId, setSelectedPriceRowId] = useState<string | null>(null);
  const [meaningfulDraftRowIds, setMeaningfulDraftRowIds] = useState<string[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const columnFilters = useMemo<ColumnFiltersState>(() => (
    query.facets.status.length > 0 ? [{ id: 'status', value: query.facets.status }] : []
  ), [query.facets.status]);
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    description: string;
    confirmLabel?: string;
    onConfirm: () => void;
  } | null>(null);

  const editorOpen = pricebookRoute?.mode === 'create' || pricebookRoute?.mode === 'edit';
  const editingId = pricebookRoute?.mode === 'edit' ? pricebookRoute.pricebookId : null;

  const loadData = async () => {
    const requestId = ++requestSequence.current;
    if (!activeTenant?.id) {
      return;
    }
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const nextPricebooks = await listPricebooks({
        catalogOwnerTenantId: activeTenant.id,
        search: searchQuery || undefined,
        status: query.facets.status,
        sort: query.sort,
        include_facets: ['status'],
        ...toLimitOffset(query.pagination),
      });
      if (requestId !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(nextPricebooks.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setPricebooks(nextPricebooks.items);
      setTotalPricebooks(nextPricebooks.total_count);
      setStatusCounts(Object.fromEntries((nextPricebooks.facets.status ?? []).map(bucket => [bucket.value, bucket.count])));
    } catch (err) {
      if (requestId !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load pricebook registry.'));
      }
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [activeTenant?.id, query.facets.status, query.pagination, query.sort, searchQuery]);

  useEffect(() => {
    if (!activeTenant?.id) {
      setCatalogs([]);
      setCatalogLoadError(null);
      return;
    }
    let cancelled = false;
    setCatalogsLoading(true);
    setCatalogLoadError(null);
    const loadCatalogOptions = async () => {
      const items: Catalog[] = [];
      const limit = 200;
      let offset = 0;
      let total = 0;
      do {
        const result = await listCatalogs({
          catalogOwnerTenantId: activeTenant.id,
          status: ['active', 'draft', 'archived'],
          sort: ['name'],
          limit,
          offset,
        });
        items.push(...result.items);
        total = result.total_count;
        offset += result.items.length;
        if (result.items.length === 0) break;
      } while (offset < total);
      return items;
    };
    void loadCatalogOptions()
      .then(result => {
        if (!cancelled) setCatalogs(result);
      })
      .catch(err => {
        if (!cancelled) {
          setCatalogs([]);
          setCatalogLoadError(getCommerceErrorMessage(err, 'Failed to load catalog options.'));
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTenant?.id]);

  const catalogOptions = useMemo<CommerceSelectOptionViewModel[]>(() => {
    const options: CommerceSelectOptionViewModel[] = catalogs
      .filter(catalog => catalog.status !== 'archived')
      .map(catalog => ({
        value: catalog.catalog_id,
        label: `${catalog.name} (${catalog.catalog_key})${catalog.status === 'draft' ? ' · Draft' : ''}`,
        description: catalog.catalog_key,
        keywords: [catalog.name, catalog.catalog_key],
      }));
    const selectedCatalogId = form.catalogId?.trim();
    if (selectedCatalogId && !options.some(option => option.value === selectedCatalogId)) {
      const selectedCatalog = catalogs.find(catalog => catalog.catalog_id === selectedCatalogId);
      options.push({
        value: selectedCatalogId,
        label: selectedCatalog
          ? `${selectedCatalog.name} (${selectedCatalog.catalog_key}) · Archived/unavailable`
          : 'Unavailable catalog',
        description: 'This saved catalog is no longer available for new scope selections.',
        disabled: true,
      });
    }
    return options;
  }, [catalogs, form.catalogId]);

  const sellingTenantOptions = useMemo(() => {
    return buildTenantSelectorOptions({
      activeTenant,
      organizations,
      observedTenantIds: pricebooks
        .map(pricebook => pricebook.selling_tenant_id?.trim() || '')
        .filter(Boolean),
    });
  }, [activeTenant, organizations, pricebooks]);

  const withCurrentQuery = useCallback((target: string) => {
    const queryIndex = navigation.path.indexOf('?');
    return queryIndex >= 0 ? `${target}${navigation.path.slice(queryIndex)}` : target;
  }, [navigation.path]);

  const performCloseEditor = () => {
    navigation.navigate(withCurrentQuery(buildPricebooksListRoute()), { replace: true });
  };

  const closeEditor = () => {
    if (meaningfulDraftRowIds.length === 0) {
      performCloseEditor();
      return;
    }
    setConfirmAction({
      title: 'Discard unsaved price drafts?',
      description: 'Your unsaved offer pricing drafts will be lost when you close this editor.',
      confirmLabel: 'Discard and close',
      onConfirm: performCloseEditor,
    });
  };

  const handleDelete = useCallback(async (pricebook: Pricebook) => {
    setError(null);
    try {
      await deletePricebook(pricebook.pricebook_id);
      await loadData();
      toast.success('Pricebook archived', {
        description: pricebook.name,
      });
      if (editingId === pricebook.pricebook_id) {
        closeEditor();
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const message = getCommerceErrorMessage(err, 'Failed to archive pricebook.');
        setError(message);
        toast.error('Failed to archive pricebook', { description: message });
      }
    }
  }, [closeEditor, editingId, loadData]);

  useEffect(() => {
    setValidationErrors({});
    setTargetHydrationNotice(null);
    setOfferTargetOptions([]);
    setFeeTargetOptions([]);
    setEditorPricebook(null);
    setActiveDestination('settings');
    setActivePriceScope('product_prices');
    setDirectoryQuery(initialDirectoryQuery);
    setExpandedProductIds([]);
    setMarkedForRemovalRowIds([]);
    setControlledFocusTarget(null);
    setPriceFilter('priced');
    setSelectedPriceRowId(null);
    setMeaningfulDraftRowIds([]);
    if (pricebookRoute?.mode === 'create') {
      setForm(emptyPricebookForm());
      setInitialForm(null);
      setTargetHydrationNotice(
        'Select a catalog scope and save the pricebook settings. Catalog offers and fees load after the saved pricebook reopens.',
      );
    }
  }, [editingId, pricebookRoute?.mode]);

  useEffect(() => {
    if (pricebookRoute?.mode !== 'edit') {
      if (!pricebookRoute || pricebookRoute.mode === 'list') setEditorLoading(false);
      return;
    }

    let cancelled = false;
    setEditorLoading(true);
    void getPricebook(pricebookRoute.pricebookId)
      .then(detail => {
        if (cancelled) return;
        const nextForm = mapPricebookToForm(detail);
        setEditorPricebook(detail);
        setForm(nextForm);
        setInitialForm(nextForm);
        setSelectedPriceRowId(null);
        setOfferTargetOptions(mapOfferTargetOptions(detail));
        setFeeTargetOptions(mapFeeTargetOptions(detail));
        setTargetHydrationNotice(
          !detail.variant_targets?.length && !detail.fee_targets?.length
            ? 'Catalog targets are not available for this pricebook yet. You can still update shell fields and metadata.'
            : null,
        );
      })
      .catch(err => {
        if (!cancelled) {
          setError(getCommerceErrorMessage(err, 'Failed to load pricebook detail.'));
          navigation.navigate(buildPricebooksListRoute(), { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) setEditorLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editingId, navigation, pricebookRoute?.mode]);

  useEffect(() => {
    if (!editorOpen || !activeTenant?.id) {
      setEstimatorOptions(initialEstimatorOptions);
      setQuoteReadinessLinks([]);
      return;
    }
    let cancelled = false;
    void loadQuotePolicyEstimatorOptions({
      selectedServiceAccountIds: form.quotePolicy.constructionEstimatorServiceAccountIds,
      canReadAutomationKeys: apiGatewayAccess.accessEnabled,
    }).then(result => {
      if (!cancelled) setEstimatorOptions(result);
    });

    const currency = (form.currency || 'USD').toUpperCase();
    void Promise.all([
      listAccessPointLayers({
        catalogOwnerTenantId: activeTenant.id,
        status: ['active'],
        purpose: ['quote_estimate', 'business_quote_estimate'],
        limit: 200,
        offset: 0,
      }),
      listServiceabilityZones({ catalogOwnerTenantId: activeTenant.id, limit: 200, offset: 0 }),
    ]).then(([layers, zones]) => {
      if (cancelled) return;
      const selectedLayer = layers.items
        .filter(layer => layer.status === 'active' && layer.mapping_source_id)
        .sort((left, right) => {
          const leftPriority = left.purpose === 'quote_estimate' ? 0 : 1;
          const rightPriority = right.purpose === 'quote_estimate' ? 0 : 1;
          return leftPriority - rightPriority || right.updated_at.localeCompare(left.updated_at);
        })[0];
      const matchingDefaults = selectedLayer?.default_build_type_rates?.filter(rate => rate.currency === currency) || [];
      const matchingZoneRates = zones.items.flatMap(zone => zone.build_type_rates || []).filter(rate => rate.currency === currency);
      setQuoteReadinessLinks([
        {
          id: 'access-point-rates',
          label: 'Access-point route defaults',
          href: selectedLayer ? `/commerce/access-points?q=${encodeURIComponent(selectedLayer.name)}` : '/commerce/access-points',
          isReady: Boolean(selectedLayer && matchingDefaults.length > 0),
          detail: selectedLayer
            ? `${selectedLayer.name}; routing source ${selectedLayer.mapping_source_id}; ${matchingDefaults.length} ${currency} build ${matchingDefaults.length === 1 ? 'rate' : 'rates'}`
            : 'Add an active quote-estimate layer with a routing source and fallback rates.',
        },
        {
          id: 'zone-rate-overrides',
          label: 'Serviceability-zone overrides',
          href: '/commerce/markets?kind=zones',
          isReady: matchingZoneRates.length > 0,
          detail: matchingZoneRates.length > 0
            ? `${matchingZoneRates.length} ${currency} zone ${matchingZoneRates.length === 1 ? 'rate' : 'rates'} configured.`
            : 'Optional: add zone-specific build rates when local construction costs differ.',
        },
      ]);
    }).catch(() => {
      if (!cancelled) {
        setQuoteReadinessLinks([
          {
            id: 'route-pricing-unavailable',
            label: 'Route pricing readiness unavailable',
            href: '/commerce/access-points',
            isReady: false,
            detail: 'Open Access Points to verify the active route-pricing configuration.',
          },
        ]);
      }
    });
    return () => { cancelled = true; };
  }, [activeTenant?.id, apiGatewayAccess.accessEnabled, editorOpen, form.currency, form.quotePolicy.constructionEstimatorServiceAccountIds.join('\u0000')]);

  const openCreateEditor = () => {
    navigation.navigate(withCurrentQuery(buildPricebookCreateRoute()));
  };

  const openEditEditor = (pricebookId: string) => {
    navigation.navigate(withCurrentQuery(buildPricebookEditorRoute(pricebookId)));
  };

  const handleRowAction = (id: string, actionId: string) => {
    if (actionId === 'edit') {
      openEditEditor(id);
    }
  };

  const handleSave = async () => {
    const initialOfferIds = new Set((initialForm?.offerPrices || []).map(row => row.offerId));
    setMeaningfulDraftRowIds(current => Array.from(new Set([
      ...current,
      ...form.offerPrices.filter(row => !initialOfferIds.has(row.offerId)).map(row => row.rowId),
    ])));
    const mutation = buildPricebookMutation({ form, initialForm, markedForRemovalRowIds });
    const validateOfferRows = !editingId
      || mutation.groups.changedOfferRowIds.length > 0
      || mutation.groups.leaseTermsChanged
      || mutation.groups.shellFields.includes('currency');
    const validateFeeRows = !editingId || mutation.groups.feePricesChanged;
    const liveOfferIds = new Set((effectiveEditorPricebook?.offer_targets || [])
      .filter(target => target.status === 'active' && target.sellable !== false)
      .map(target => target.offer_id));
    const initialOffersById = new Map((initialForm?.offerPrices || []).map(row => [row.offerId, row]));
    const liveFeeIds = new Set((effectiveEditorPricebook?.fee_targets || []).map(target => target.fee_id));
    const initialFeesById = new Map((initialForm?.feePrices || []).map(row => [row.feeId, row]));
    const removedRowIds = new Set(markedForRemovalRowIds);
    const nextErrors = {
      ...validateForm(form, {
      validateOfferRows,
      validateFeeRows,
      shouldValidateOfferRow: row => {
        if (removedRowIds.has(row.rowId)) return false;
        if (liveOfferIds.has(row.offerId)) return true;
        const initialRow = initialOffersById.get(row.offerId);
        return !initialRow || JSON.stringify(row) !== JSON.stringify(initialRow);
      },
      shouldValidateFeeRow: row => {
        if (liveFeeIds.has(row.feeId)) return true;
        const initialRow = initialFeesById.get(row.feeId);
        return !initialRow || JSON.stringify(row) !== JSON.stringify(initialRow);
      },
      }),
      ...quotePolicyValidationErrors(form, estimatorOptions),
    };
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const message = 'Resolve the highlighted pricebook validation errors before saving.';
      setError(message);
      toast.error('Pricebook not saved', { description: message });
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = mutation.payload;
      if (editingId) {
        const updated = await updatePricebook(editingId, payload);
        toast.success('Pricebook updated', { description: form.name });
        const nextForm = mapPricebookToForm(updated);
        setForm(nextForm);
        setInitialForm(nextForm);
        setEditorPricebook(updated);
        setMeaningfulDraftRowIds([]);
        setMarkedForRemovalRowIds([]);
        await loadData();
        performCloseEditor();
      } else {
        const created = await createPricebook(payload);
        await loadData();
        if (created?.pricebook_id) {
          // Stay in the editor: reopening the saved pricebook hydrates the
          // catalog offer/fee targets so pricing rows can be added immediately.
          toast.success('Pricebook created', {
            description: `${form.name} — add offer and fee pricing next.`,
          });
          navigation.navigate(withCurrentQuery(buildPricebookEditorRoute(created.pricebook_id)), { replace: true });
        } else {
          toast.success('Pricebook created', { description: form.name });
          performCloseEditor();
        }
      }
    } catch (err) {
      const msg = getCommerceErrorMessage(err, 'Failed to save pricebook.');
      const status = getCommerceErrorStatus(err);
      if (
        mutation.groups.shellFields.includes('catalogId')
        && status !== null
        && status >= 400
        && status < 500
      ) {
        setValidationErrors(current => ({ ...current, catalogId: msg }));
        setActiveDestination('settings');
      }
      setError(msg);
      toast.error('Failed to save pricebook', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  const catalogScopeChanged = initialForm !== null
    && (form.catalogId?.trim() || '') !== (initialForm.catalogId?.trim() || '');
  const effectiveOfferTargetOptions = catalogScopeChanged ? [] : offerTargetOptions;
  const effectiveFeeTargetOptions = catalogScopeChanged ? [] : feeTargetOptions;
  const effectiveEditorPricebook = catalogScopeChanged && editorPricebook
    ? { ...editorPricebook, offer_targets: [], variant_targets: [], fee_targets: [] }
    : editorPricebook;

  const coverage = useMemo(() => {
    const offerTotal = effectiveOfferTargetOptions.length;
    const feeTotal = effectiveFeeTargetOptions.length;
    const offerLabel = offerTotal > 0
      ? `${form.offerPrices.length}/${offerTotal} offers priced`
      : `${form.offerPrices.length} offers priced`;
    const feeLabel = feeTotal > 0
      ? `${form.feePrices.length}/${feeTotal} fees priced`
      : `${form.feePrices.length} fees priced`;

    const missingLabels = [
      offerTotal > form.offerPrices.length
        ? `${offerTotal - form.offerPrices.length} offer targets still unpriced`
        : null,
      feeTotal > form.feePrices.length ? `${feeTotal - form.feePrices.length} fee targets still unpriced` : null,
      !offerTotal && !feeTotal ? targetHydrationNotice : null,
    ].filter(Boolean);

    return {
      offerCoverageLabel: offerLabel,
      feeCoverageLabel: feeLabel,
      offerCoverageComplete: offerTotal > 0 && form.offerPrices.length >= offerTotal,
      feeCoverageComplete: feeTotal > 0 && form.feePrices.length >= feeTotal,
      missingRowsLabel: missingLabels.length > 0 ? missingLabels.join(' • ') : undefined,
      overrideScopeLabel: form.sellingTenantId?.trim() ? 'Selling tenant override' : 'Catalog owner default',
    };
  }, [
    effectiveFeeTargetOptions.length,
    form.feePrices.length,
    form.offerPrices.length,
    form.sellingTenantId,
    effectiveOfferTargetOptions.length,
    targetHydrationNotice,
  ]);

  const editorCallouts = useMemo<CommerceHelpCalloutViewModel[]>(() => {
    const callouts: CommerceHelpCalloutViewModel[] = [];
    if (targetHydrationNotice) {
      callouts.push({
        id: 'target-hydration',
        title: 'Catalog Target Availability',
        body: targetHydrationNotice,
        tone: 'warning',
      });
    }
    if (catalogScopeChanged) {
      callouts.push({
        id: 'catalog-scope-pending',
        title: 'Catalog scope change pending',
        body: 'Save the Settings change to validate retained prices and reload targets for the selected catalog.',
        tone: 'info',
      });
    }
    if (catalogLoadError) {
      callouts.push({
        id: 'catalog-options-error',
        title: 'Catalog options unavailable',
        body: catalogLoadError,
        tone: 'warning',
      });
    }
    callouts.push({
      id: 'pricing-semantics',
      title: 'Pricing Rows',
      body: 'Add offer and fee rows to set what customers pay. Use offer rows when the same merchandise variant has multiple commercial acquisition modes such as rent and purchase.',
      tone: 'info',
    });
    return callouts;
  }, [catalogLoadError, catalogScopeChanged, targetHydrationNotice]);

  const mutation = useMemo(
    () => buildPricebookMutation({ form, initialForm, markedForRemovalRowIds }),
    [form, initialForm, markedForRemovalRowIds],
  );
  const validationIssues = useMemo(
    () => buildPricebookValidationIssues(form, validationErrors),
    [form, validationErrors],
  );
  const catalogLabelById = useMemo(
    () => new Map(catalogs.map(catalog => [
      catalog.catalog_id,
      `${catalog.name} (${catalog.catalog_key})${catalog.status === 'draft' ? ' · Draft' : catalog.status === 'archived' ? ' · Archived' : ''}`,
    ])),
    [catalogs],
  );
  const directoryResult = useMemo(() => buildPricebookDirectory({
    pricebook: effectiveEditorPricebook || ({ offer_targets: [] } as unknown as Pricebook),
    form,
    initialForm,
    validationErrors,
    markedForRemovalRowIds,
    query: directoryQuery,
    expandedProductIds,
    selectedPriceRowId,
  }), [
    directoryQuery,
    effectiveEditorPricebook,
    expandedProductIds,
    form,
    initialForm,
    markedForRemovalRowIds,
    selectedPriceRowId,
    validationErrors,
  ]);
  const untouchedDraftRowIds = useMemo(() => {
    const initialOfferIds = new Set((initialForm?.offerPrices || []).map(row => row.offerId));
    const meaningfulIds = new Set(meaningfulDraftRowIds);
    return form.offerPrices
      .filter(row => !initialOfferIds.has(row.offerId) && !meaningfulIds.has(row.rowId))
      .map(row => row.rowId);
  }, [form.offerPrices, initialForm, meaningfulDraftRowIds]);
  const reviewChangeGroups = useMemo(() => {
    const base = buildPricebookReviewChangeGroups({
      form,
      initialForm,
      markedForRemovalRowIds,
      validationIssues,
      ignoredDraftRowIds: untouchedDraftRowIds,
      catalogLabels: Object.fromEntries(catalogLabelById),
    });
    const quotePolicyGroup = buildQuotePolicyReviewChangeGroup(form.quotePolicy, initialForm?.quotePolicy);
    const mappedQuotePolicyGroup = {
      id: 'quote_policy' as const,
      label: quotePolicyGroup.label,
      count: quotePolicyGroup.count,
      items: quotePolicyGroup.items.map(item => ({
        ...item,
        kind: 'quote_policy' as const,
      })),
    };
    const blockingIndex = base.findIndex(group => group.id === 'blocking_issues');
    return blockingIndex < 0
      ? [...base, mappedQuotePolicyGroup]
      : [...base.slice(0, blockingIndex), mappedQuotePolicyGroup, ...base.slice(blockingIndex)];
  }, [catalogLabelById, form, initialForm, markedForRemovalRowIds, untouchedDraftRowIds, validationIssues]);
  const quotePolicyViewModel = useMemo(() => buildQuotePolicyViewModel({
    value: form.quotePolicy,
    initialValue: initialForm?.quotePolicy,
    estimatorOptions,
    readinessLinks: quoteReadinessLinks,
  }), [estimatorOptions, form.quotePolicy, initialForm?.quotePolicy, quoteReadinessLinks]);
  const dirtyCount = useMemo(() => reviewChangeGroups
    .filter(group => group.id !== 'blocking_issues')
    .reduce((total, group) => total + group.count, 0), [reviewChangeGroups]);
  const revalidateEditedForm = (nextForm: PricebookEditorFormValue) => {
    const liveOfferIds = new Set((effectiveEditorPricebook?.offer_targets || [])
      .filter(target => target.status === 'active' && target.sellable !== false)
      .map(target => target.offer_id));
    const liveFeeIds = new Set((effectiveEditorPricebook?.fee_targets || []).map(target => target.fee_id));
    const initialOffersById = new Map((initialForm?.offerPrices || []).map(row => [row.offerId, row]));
    const initialFeesById = new Map((initialForm?.feePrices || []).map(row => [row.feeId, row]));
    const removedRowIds = new Set(markedForRemovalRowIds);
    const meaningfulDraftIds = new Set(meaningfulDraftRowIds);
    return {
      ...validateForm(nextForm, {
      validateOfferRows: true,
      validateFeeRows: true,
      shouldValidateOfferRow: row => {
        if (removedRowIds.has(row.rowId)) return false;
        const initialRow = initialOffersById.get(row.offerId);
        if (liveOfferIds.has(row.offerId)) return Boolean(initialRow) || meaningfulDraftIds.has(row.rowId);
        return !initialRow || JSON.stringify(row) !== JSON.stringify(initialRow);
      },
      shouldValidateFeeRow: row => {
        if (liveFeeIds.has(row.feeId)) return true;
        const initialRow = initialFeesById.get(row.feeId);
        return !initialRow || JSON.stringify(row) !== JSON.stringify(initialRow);
      },
      }),
      ...quotePolicyValidationErrors(nextForm, estimatorOptions),
    };
  };

  const discardUntouchedSelectedDraft = (nextRowId: string | null) => {
    if (!selectedPriceRowId || selectedPriceRowId === nextRowId) return form;
    const selectedIndex = form.offerPrices.findIndex(row => row.rowId === selectedPriceRowId);
    if (selectedIndex < 0 || meaningfulDraftRowIds.includes(selectedPriceRowId)) return form;
    const selectedRow = form.offerPrices[selectedIndex];
    if (initialForm?.offerPrices.some(row => row.offerId === selectedRow.offerId)) return form;
    const nextForm = {
      ...form,
      offerPrices: form.offerPrices.filter((_, index) => index !== selectedIndex),
    };
    setForm(nextForm);
    setValidationErrors(current => remapOfferPriceValidationAfterDiscard(current, selectedIndex));
    return nextForm;
  };

  const openOfferPrice = (offerId: string) => {
    const existingRow = form.offerPrices.find(row => row.offerId === offerId);
    if (existingRow) {
      discardUntouchedSelectedDraft(existingRow.rowId);
      setSelectedPriceRowId(existingRow.rowId);
      return;
    }
    const sourceForm = discardUntouchedSelectedDraft(null);
    const draft = buildDraftOfferPrice({
      offerId,
      offerTargets: effectiveEditorPricebook?.offer_targets,
      existingRows: sourceForm.offerPrices,
      currency: sourceForm.currency || 'USD',
    });
    if (!draft) return;
    setForm({ ...sourceForm, offerPrices: [...sourceForm.offerPrices, draft] });
    setSelectedPriceRowId(draft.rowId);
  };

  const discardPriceDraft = (rowId: string) => {
    const rowIndex = form.offerPrices.findIndex(row => row.rowId === rowId);
    if (rowIndex < 0) return;
    const row = form.offerPrices[rowIndex];
    if (initialForm?.offerPrices.some(initialRow => initialRow.offerId === row.offerId)) return;
    setForm({ ...form, offerPrices: form.offerPrices.filter((_, index) => index !== rowIndex) });
    setMeaningfulDraftRowIds(current => current.filter(id => id !== rowId));
    setMarkedForRemovalRowIds(current => current.filter(id => id !== rowId));
    setValidationErrors(current => remapOfferPriceValidationAfterDiscard(current, rowIndex));
    if (selectedPriceRowId === rowId) setSelectedPriceRowId(null);
  };

  const selectPriceRow = (rowId: string | null) => {
    discardUntouchedSelectedDraft(rowId);
    setSelectedPriceRowId(rowId);
  };

  const selectValidationIssue = (issueId: string) => {
    const issue = validationIssues.find(candidate => candidate.id === issueId);
    if (!issue) return;
    setActiveDestination(issue.destination === 'review' ? 'review' : issue.destination);
    if (issue.scope) setActivePriceScope(issue.scope);
    if (issue.rowId) {
      setSelectedPriceRowId(issue.rowId);
      setPriceFilter('attention');
      setDirectoryQuery(current => normalizePricebookDirectoryQuery({ ...current, view: 'needs_attention' }));
    }
    setControlledFocusTarget({
      requestId: `${issue.id}:${Date.now()}`,
      destination: issue.destination,
      scope: issue.scope,
      rowId: issue.rowId,
      field: issue.field,
    });
  };

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'pricebooks',
    searchQuery,
    searchPlaceholder: 'Search pricebooks...',
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to manage pricebooks.',
    onSelectSection: sectionId => navigation.navigate(buildCommerceSectionRoute(sectionId)),
    primaryAction: {
      id: 'create-pricebook',
      label: 'New Pricebook',
      onAction: () => {
        openCreateEditor();
      },
    },
  });

  const columns = useMemo<ColumnDef<Pricebook>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      size: 300,
      header: 'Name',
      cell: ({ row }) => {
        const pricebook = row.original;
        return (
          <button
            type="button"
            onClick={() => handleRowAction(pricebook.pricebook_id, 'edit')}
            className="group min-w-0 text-left"
          >
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {pricebook.name}
            </p>
            <DescriptionText className="line-clamp-1 text-[11px]">
              {[
                getOfferPriceCount(pricebook)
                  ? `${getOfferPriceCount(pricebook)} ${getOfferPriceCount(pricebook) === 1 ? 'offer' : 'offers'} priced`
                  : null,
                pricebook.fee_prices?.length
                  ? `${pricebook.fee_prices.length} ${pricebook.fee_prices.length === 1 ? 'fee' : 'fees'} priced`
                  : null,
              ].filter(Boolean).join(' • ') || 'No offer or fee pricing rows'}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'catalog_id',
      size: 180,
      header: 'Catalog',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {row.original.catalog_id
            ? catalogLabelById.get(row.original.catalog_id) || row.original.catalog_id
            : 'All catalogs'}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      size: 100,
      header: 'Status',
      cell: ({ row }) => (
        <RegistryStatus label={row.original.status} variant={row.original.status === 'active' ? 'success' : 'muted'} />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: 'coverage',
      accessorFn: pricebook =>
        pricebook.readiness_label ||
        pricebook.coverage?.variant_coverage_label ||
        pricebook.coverage?.fee_coverage_label ||
        '',
      size: 220,
      header: 'Coverage',
      cell: ({ row }) => {
        const pricebook = row.original;
        const missing = pricebook.coverage?.missing_rows_label;
        const label =
          pricebook.readiness_label ||
          [pricebook.coverage?.variant_coverage_label, pricebook.coverage?.fee_coverage_label]
            .filter(Boolean)
            .join(' · ') ||
          (getOfferPriceCount(pricebook)
            ? `${getOfferPriceCount(pricebook)} priced`
            : 'Nothing priced yet');
        return missing ? (
          <span
            title={missing}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground"
          >
            <AlertTriangle className="h-3 w-3 shrink-0" />
            {label}
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground">{label}</span>
        );
      },
    },
    {
      accessorKey: 'pricing_strategy',
      size: 150,
      header: 'Strategy',
      cell: ({ row }) => (
        <span className="rounded border border-border/50 px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
          {row.original.pricing_strategy || 'standard'}
        </span>
      ),
    },
    {
      accessorKey: 'currency',
      size: 80,
      header: 'Currency',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.currency || 'USD'}</span>
      ),
    },
    {
      accessorKey: 'selling_tenant_id',
      size: 150,
      header: 'Tenant',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {row.original.selling_tenant_id || 'Owner default'}
        </span>
      ),
    },
    {
      accessorKey: 'updated_at',
      size: 120,
      header: 'Updated',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {new Date(row.original.updated_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const pricebook = row.original;
        return (
          <HorizontalStack gap={2} className="justify-end shrink-0">
            <button
              type="button"
              onClick={() => handleRowAction(pricebook.pricebook_id, 'edit')}
              aria-label={`Edit ${pricebook.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() =>
                setConfirmAction({
                  title: 'Archive pricebook?',
                  description: `"${pricebook.name}" will stop applying to selling flows. This can be recreated but not undone here.`,
                  onConfirm: () => void handleDelete(pricebook),
                })
              }
              aria-label={`Archive ${pricebook.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-destructive/50 hover:bg-destructive/5 hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </HorizontalStack>
        );
      },
    },
  ], [catalogLabelById, handleDelete]);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: PRICEBOOK_SORT_FIELDS, registry: 'pricebooks' });

  const editorViewModel: PricebookEditorViewModel = {
    mode: editingId ? 'edit' : 'create',
    state: saving ? 'saving' : editorLoading || catalogsLoading ? 'loading' : 'ready',
    isOpen: editorOpen,
    title: editingId ? 'Edit Pricebook' : 'New Pricebook',
    subtitle: 'Set product prices and catalog fees without pricing every catalog offer.',
    saveActionLabel: editingId ? 'Update Pricebook' : 'Create Pricebook',
    pricebookId: editingId,
    value: form,
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'active', label: 'Active' },
      { value: 'archived', label: 'Archived' },
    ],
    currencyOptions: [
      { value: 'USD', label: 'US Dollar' },
      { value: 'CAD', label: 'Canadian Dollar' },
    ],
    catalogOptions,
    sellingTenantOptions,
    pricingStrategyOptions: [
      { value: 'standard', label: 'Standard Pricing' },
      { value: 'promotional', label: 'Promotional Override' },
      { value: 'reseller_override', label: 'Reseller Override' },
    ],
    chargeTypeOptions: [
      { value: 'one_time', label: 'One-time' },
      { value: 'recurring', label: 'Recurring' },
      { value: 'percentage', label: 'Percentage' },
      { value: 'included', label: 'Included' },
    ],
    billingPeriodOptions: [
      { value: 'month', label: 'Monthly' },
      { value: 'quarter', label: 'Quarterly' },
      { value: 'year', label: 'Yearly' },
      { value: 'week', label: 'Weekly' },
    ],
    offerTargetOptions: effectiveOfferTargetOptions,
    feeTargetOptions: effectiveFeeTargetOptions,
    quotePolicy: quotePolicyViewModel,
    coverage,
    fieldHelp: [
      {
        field: 'catalogId',
        label: 'Catalog scope',
        body: 'Choose one catalog to limit this pricebook to its products and offers, or leave All catalogs selected for owner-wide compatibility.',
        tone: 'info',
      },
      {
        field: 'sellingTenantId',
        label: 'Selling tenant override',
        body: 'Leave blank to apply the pricebook at the catalog owner level.',
        tone: 'info',
      },
    ],
    callouts: editorCallouts,
    validationErrors,
    activeDestination,
    activePriceScope,
    directoryQuery,
    directoryCounts: directoryResult.counts,
    productGroups: directoryResult.productGroups,
    expandedProductIds,
    selectedInspector: directoryResult.selectedInspector,
    markedForRemovalRowIds,
    controlledFocusTarget,
    reviewChangeGroups,
    mutationGroups: mutation.groups,
    validationIssues,
    dirtyCount,
    priceFilter,
    selectedPriceRowId,
    onChange: patch => {
      const nextForm = { ...form, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'catalogId')) {
        setSelectedPriceRowId(null);
        setExpandedProductIds([]);
        setValidationErrors(current => {
          const { catalogId: _catalogError, ...remaining } = current;
          return remaining;
        });
      }
      if (patch.offerPrices) {
        const initialOfferIds = new Set((initialForm?.offerPrices || []).map(row => row.offerId));
        const currentRowsById = new Map(form.offerPrices.map(row => [row.rowId, row]));
        const nextRowIds = new Set(patch.offerPrices.map(row => row.rowId));
        setMeaningfulDraftRowIds(current => Array.from(new Set([
          ...current.filter(rowId => nextRowIds.has(rowId)),
          ...patch.offerPrices.filter(row => {
            if (initialOfferIds.has(row.offerId)) return false;
            const currentRow = currentRowsById.get(row.rowId);
            return !currentRow || JSON.stringify(currentRow) !== JSON.stringify(row);
          }).map(row => row.rowId),
        ])));
      }
      setForm(nextForm);
      if (Object.keys(validationErrors).length > 0) {
        setValidationErrors(revalidateEditedForm(nextForm));
      }
    },
    onDestinationChange: destination => {
      if (destination !== 'prices') {
        const nextForm = discardUntouchedSelectedDraft(null);
        if (nextForm !== form) setSelectedPriceRowId(null);
      }
      setActiveDestination(destination);
    },
    onPriceScopeChange: scope => {
      if (scope !== 'product_prices') {
        const nextForm = discardUntouchedSelectedDraft(null);
        if (nextForm !== form) setSelectedPriceRowId(null);
      }
      setActivePriceScope(scope);
    },
    onDirectoryQueryChange: patch => {
      setDirectoryQuery(current => normalizePricebookDirectoryQuery({ ...current, ...patch }));
    },
    onToggleProductExpanded: productId => {
      setExpandedProductIds(current => toggleExpandedProductId(current, productId));
    },
    onMarkPriceRowForRemoval: rowId => {
      setMarkedForRemovalRowIds(current => current.includes(rowId) ? current : [...current, rowId]);
    },
    onDiscardPriceDraft: discardPriceDraft,
    onUndoPriceRowRemoval: rowId => {
      setMarkedForRemovalRowIds(current => current.filter(id => id !== rowId));
    },
    onControlledFocusHandled: requestId => {
      setControlledFocusTarget(current => current?.requestId === requestId ? null : current);
    },
    onPriceFilterChange: filter => {
      setPriceFilter(filter);
      const view = filter === 'unpriced' ? 'not_in_pricebook' : filter === 'attention' ? 'needs_attention' : 'priced';
      setDirectoryQuery(current => normalizePricebookDirectoryQuery({ ...current, view }));
    },
    onOpenOfferPrice: openOfferPrice,
    onSelectPriceRow: selectPriceRow,
    onSelectValidationIssue: selectValidationIssue,
    onQuotePolicyNavigate: href => navigation.navigate(href),
    onSave: handleSave,
    onClose: closeEditor,
  };

  return (
    <div className="flex h-full flex-col">
      <CommerceCatalogWorkspace
        viewModel={shellViewModel}
        padded={false}
        registryContent={
          <RegistryWorkspace>
            <RegistryTableSurface>
              <DataTable
                mode="manual"
                columns={columns}
                data={pricebooks}
                sorting={sorting}
                onSortingChange={updater => {
                  const next = typeof updater === 'function' ? updater(sorting) : updater;
                  query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
                }}
                globalFilter={searchQuery}
                columnFilters={columnFilters}
                onColumnFiltersChange={updater => {
                  const next = typeof updater === 'function' ? updater(columnFilters) : updater;
                  const filter = next.find(item => item.id === 'status');
                  query.setFacet('status', Array.isArray(filter?.value) ? filter.value.map(String) : []);
                }}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                pagination={query.pagination}
                onPaginationChange={query.onPaginationChange}
                manualPagination
                totalRowCount={totalPricebooks}
                loading={loading}
                className="border-none rounded-none"
                paginationClassName="px-0 py-0"
                showPagination
                emptyState={
                  <div className="px-0 py-12 text-center">
                    <p className="text-sm font-black text-foreground">No Pricebooks Defined</p>
                    <DescriptionText className="mt-1 text-xs">
                      {searchQuery || query.facets.status.length > 0
                        ? 'No pricebooks matched the current search or filters.'
                        : 'Create a pricebook to define commercial pricing for your offers and fees.'}
                    </DescriptionText>
                    {searchQuery || query.facets.status.length > 0 ? (
                      <button type="button" onClick={query.clear} className="mt-3 text-xs font-semibold text-primary hover:underline">
                        Reset search and filters
                      </button>
                    ) : null}
                  </div>
                }
                renderToolbar={table => (
                  <DataTableToolbar
                    table={table}
                    searchValue={searchQuery}
                    onSearchChange={query.setSearch}
                    searchPlaceholder="Search all pricebooks..."
                    resultCount={totalPricebooks}
                    className="border-b border-border/40 bg-transparent px-0 py-0"
                    filters={
                      <DataTableFacetedFilter
                        title="Status"
                        selectedValues={new Set(query.facets.status)}
                        onFilterChange={values => query.setFacet('status', Array.from(values))}
                        counts={statusCounts}
                        options={[
                          { label: 'Active', value: 'active' },
                          { label: 'Draft', value: 'draft' },
                          { label: 'Archived', value: 'archived' },
                        ]}
                      />
                    }
                  />
                )}
              />
            </RegistryTableSurface>
          </RegistryWorkspace>
        }
        takeoverActive={editorOpen}
        replaceShellHeaderWhenTakeoverActive
        takeoverContent={<PricebookDesignerWorkspace viewModel={editorViewModel} />}
        titleContent={
          <CommerceBreadcrumbHeader
            breadcrumbs={[{ label: 'Pricebooks' }]}
            title="Pricebooks"
            description="Define commercial pricing rules, coverage, and selling-tenant overrides for each catalog owner tenant."
          />
        }
      />

      {confirmAction ? (
        <>
          <div
            className="fixed inset-0 z-[60] bg-background/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setConfirmAction(null)}
            aria-hidden="true"
          />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-label={confirmAction.title}
            className="fixed left-1/2 top-1/2 z-[70] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-5 shadow-2xl animate-in fade-in zoom-in-95 duration-200"
          >
            <p className="text-sm font-semibold text-foreground">{confirmAction.title}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
              {confirmAction.description}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                className="rounded-md border border-border/60 bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted/40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const action = confirmAction.onConfirm;
                  setConfirmAction(null);
                  action();
                }}
                className="rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground transition-opacity hover:opacity-90"
              >
                {confirmAction.confirmLabel || 'Archive'}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
